import { providerApi } from '@/api/ProviderApi'
import type { VrdhSchedulingSlot } from '@/api/types/ProviderApi'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { groupBy, isEmpty } from 'ramda'
import { useMemo } from 'react'

const SLOT_DATE_CHUNK_SIZE = 5

const chunkSlotDates = (slots: Record<string, VrdhSchedulingSlot[]> = {}) => {
	if (isEmpty(slots) || isEmpty(Object.keys(slots))) {
		return []
	}

	return Object.keys(slots).reduce(
		(acc: Record<string, VrdhSchedulingSlot[]>[], date, index) => {
			const isNewChunk = index % SLOT_DATE_CHUNK_SIZE === 0
			const currentSlots = slots[date]

			if (isNewChunk) {
				acc.push({ [date]: currentSlots ?? [] })
			} else {
				const lastChunk = acc[acc.length - 1]
				if (lastChunk) {
					lastChunk[date] = currentSlots ?? []
				}
			}

			return acc
		},
		[]
	)
}

export const useFetchOpenVrdhSlots = () => {
	const { data: openSlots = [], isLoading } = useQuery({
		queryKey: ['fetch-open-vrdh-slots'],
		queryFn: () => providerApi.fetchVrdhSchedulingSlots(),
	})

	const slotsByDate = useMemo(() => {
		return groupBy(
			(slot) => format(slot.startTimeUtc, 'E d LLL'),
			openSlots ?? []
		) as Record<string, VrdhSchedulingSlot[]>
	}, [openSlots])

	const chunkedSlotsByDate = useMemo(
		() => chunkSlotDates(slotsByDate ?? {}),
		[slotsByDate]
	)

	return {
		openSlots,
		slotsByDate: slotsByDate ?? [],
		chunkedSlotsByDate,
		isLoadingSlots: isLoading,
	}
}
